/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
    $createTextNode,
    $getSelection,
    $isRangeSelection,
} from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { makeStyles } from 'tss-react/mui';

const styles = (theme) => ({
    emojiMenu: {
        backgroundColor: 'white',
        width: '55%',
        maxHeight: "10vh",
        boxShadow: 'rgba(0, 0, 0, 0.117647) 0px 1px 6px, rgba(0, 0, 0, 0.117647) 0px 1px 4px',
        borderRadius: '4px',
        padding: "0",
        position: 'absolute', // Position for overlay
        zIndex: 1500, // Ensure it's above other elements
        top: '-30px',
        left: '40%',
        overflow: "scroll",
        display: "block"
    },
    emojiMenuList: {
        margin: "0",
        padding: "0",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%",
    },
    emojiMenuItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        zIndex: `50 !important`,
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    selectedEmojiMenuItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        backgroundColor: '#f5f5f5',

        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
});
const useStyles = makeStyles()(styles);

class EmojiOption extends MenuOption {
    title;
    emoji;
    keywords;

    constructor(
        title,
        emoji,
        options = { keywords: [] },
    ) {
        super(title);
        this.title = title;
        this.emoji = emoji;
        this.keywords = options.keywords || [];
    }
}
function EmojiMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}) {
    const { classes } = useStyles();
    return (
        <li
            key={option.emoji}
            tabIndex={-1}
            className={isSelected ? classes.selectedEmojiMenuItem : classes.emojiMenuItem}
            role="option"
            aria-selected={isSelected}
            id={'emoji-item-' + index}
            onMouseEnter={onMouseEnter}
            onClick={onClick}>
            <span className="text">
                {option.emoji}
            </span>
        </li>
    );
}

export default function EmojiPickerPlugin({ emojiPickerOpen, setEmojiPickerOpen }) {
    const [editor] = useLexicalComposerContext();
    const [emojis, setEmojis] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const { classes } = useStyles();

    useEffect(() => {
        import('./utils/EmojiList').then((file) => setEmojis(file.default));
    }, []);

    const emojiOptions = useMemo(
        () =>
            emojis != null
                ? emojis.map(
                    ({ emoji, aliases, tags }) =>
                        new EmojiOption(aliases[0], emoji, {
                            keywords: [...aliases, ...tags],
                        }),
                )
                : [],
        [emojis],
    );

    const onSelectOption = useCallback(
        (selectedOption) => {
            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selectedOption) {
                    return;
                }
                selection.insertNodes([$createTextNode(selectedOption.emoji)]);
                setEmojiPickerOpen(false);
            });
        },
        [editor, setEmojiPickerOpen],
    );

    const handleEmojiClick = (index) => {
        const selectedOption = emojiOptions[index];
        if (selectedOption) {
            onSelectOption(selectedOption);
        }
    };

    if (!emojiPickerOpen || emojiOptions.length === 0) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={classes.emojiMenu}>
            <ul className={classes.emojiMenuList}>
                {emojiOptions.map((option, index) => (
                    <EmojiMenuItem
                        key={option.emoji}
                        index={index}
                        isSelected={selectedIndex === index}
                        onClick={() => {
                            handleEmojiClick(index);
                        }}
                        onMouseEnter={() => {
                            setSelectedIndex(index);
                        }}
                        option={option}
                    />
                ))}
            </ul>
        </div>,
        document.getElementById('lexical-editor'),
    );
}
